import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import Card from '@/components/UI/Card';
import { PageInfo } from '@/interfaces/common.interface';
import {
  ISuggestedProductsRow,
  ISuggestedSummary,
} from '@/interfaces/reportOverView.interface';
import {
  formatDateTime,
  GetFilterUrl,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import {
  Download,
  LucideIcon,
  Package,
  Percent,
  ShoppingBag,
  Sparkles,
} from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Range } from 'react-date-range';

interface RecommendationSalesReportProps {
  selectedValue: string;
  dateRange: Range[];
  refreshTrigger: number;
}

interface SummaryCardItem {
  title: string;
  value: string;
  icon: LucideIcon;
  iconBg: string;
  iconColor: string;
}

const defaultSummary: ISuggestedSummary = {
  totalUnitsSold: 0,
  totalRevenue: 0,
  totalOrdersCount: 0,
  recommendationOrdersCount: 0,
  conversionRate: 0,
};

const RecommendationSalesReport = ({
  selectedValue,
  dateRange,
  refreshTrigger,
}: RecommendationSalesReportProps) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState<ISuggestedSummary>(defaultSummary);
  const [products, setProducts] = useState<ISuggestedProductsRow[]>([]);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    total: 0,
    totalPages: 1,
    page: 1,
    limit: 1,
  });

  const buildFilterQuery = (page: number) => {
    const startDate =
      selectedValue === 'custom' || selectedValue === ''
        ? formatDateTime(dateRange[0].startDate, 'YYMMDD')
        : '';
    const endDate =
      selectedValue === 'custom' || selectedValue === ''
        ? formatDateTime(dateRange[0].endDate, 'YYMMDD')
        : '';

    return {
      from: startDate,
      to: endDate,
      type: selectedValue,
      page,
    };
  };

  const fetchRecommendationSales = async (currentPage: number) => {
    try {
      if (!dateRange?.[0]?.startDate || !dateRange?.[0]?.endDate) {
        return;
      }

      setLoading(true);
      const filterQuery = buildFilterQuery(currentPage);
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.reports}/recommendation-sales?${query}`;
      const res = await callApi<{
        summary: ISuggestedSummary;
        products: {
          rows: ISuggestedProductsRow[];
          pageInfo: PageInfo;
        };
      }>(PATH, { method: 'GET' });

      setSummary(res.summary || defaultSummary);
      setProducts(res.products?.rows || []);
      setPageInfo({
        total: res.products?.pageInfo?.total || 0,
        totalPages: res.products?.pageInfo?.totalPages || 1,
        page: res.products?.pageInfo?.page || 1,
        limit: res.products?.pageInfo?.limit || 10,
      });
    } catch (error) {
      ThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadRecommendationReport = async () => {
    try {
      setDownloading(true);
      const filterQuery = buildFilterQuery(pageInfo.page);
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.reports}/download-recommendation-report?${query}`;
      const response = await callApi(PATH, {
        method: 'POST',
        responseType: 'arraybuffer',
      });

      if (response instanceof ArrayBuffer) {
        const blob = new Blob([response], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const formattedDateTime = moment().format('YYYY-MM-DD_HH-mm-ss');
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `recommendation_report_${formattedDateTime}.xlsx`;
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        showToastNotification('danger', 'Failed to download the report');
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [selectedValue, refreshTrigger]);

  useEffect(() => {
    fetchRecommendationSales(page);
  }, [selectedValue, refreshTrigger, page]);

  const summaryCards: SummaryCardItem[] = [
    {
      title: 'Total Units Sold',
      value: `${summary.totalUnitsSold}`,
      icon: Package,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
    },
    {
      title: 'Total Revenue',
      value: `BHD ${toFixedThree(summary.totalRevenue)}`,
      icon: ShoppingBag,
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-500',
    },
 
    {
      title: 'Recommendation Orders',
      value: `${summary.recommendationOrdersCount}`,
      icon: Sparkles,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
    },
    {
      title: 'Conversion Rate',
      value: `${summary.conversionRate}%`,
      icon: Percent,
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-500',
    },
  ];

  const columns: TableColumn<ISuggestedProductsRow>[] = [
    {
      key: 'name',
      header: 'Product Name',
    },
    {
      key: 'timesSold',
      header: 'Times Sold',
    },
    {
      key: 'totalQuantity',
      header: 'Total Quantity',
    },
    {
      key: 'totalRevenue',
      header: 'Total Revenue (BHD)',
      render: (item) => toFixedThree(item.totalRevenue),
    },
  ];

  return (
    <Card className="mt-5">
      <div className="flex items-center justify-between gap-3 mb-6">
        <PageHeading text="Recommendation Sales" textClass="mb-0!" />
        <button
          type="button"
          onClick={downloadRecommendationReport}
          disabled={downloading || loading}
          className="btn-secondary p-2! flex items-center justify-center disabled:opacity-50"
          title="Download Recommendation Report"
        >
          <Download size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.title}
              className="rounded-2xl border border-gray-300/15 bg-gray-50/80 p-4 flex flex-col gap-3 min-w-0"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${card.iconBg}`}
                >
                  <Icon size={18} className={card.iconColor} />
                </div>
                <p className="text-xs text-gray-500 font-medium leading-tight truncate">
                  {card.title}
                </p>
              </div>
              {loading ? (
                <div className="h-7 w-24 rounded-md bg-gray-200 animate-pulse" />
              ) : (
                <p className="text-lg font-semibold text-black truncate">
                  {card.value}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <CommonTable
        columns={columns}
        data={products}
        getItemId={(item) => item.itemId}
        loading={loading}
        emptyMessage="No recommendation sales data available"
        minHeight="min-h-48"
      />
      {pageInfo.totalPages > 1 && (
        <Pagination
          paginationData={pageInfo}
          onPageChange={(nextPage) => setPage(nextPage)}
          isLoading={loading}
        />
      )}
    </Card>
  );
};

export default RecommendationSalesReport;
